import React, { useCallback, useEffect, useState } from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Columns2,
  Square,
  X,
} from 'lucide-react';

const WIDTH_PRESETS = [320, 480, 640, 800, 960];
const NUDGE_STEP = 8;
const NUDGE_STEP_FINE = 1;

function parseDimensionInput(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return null;
  const value = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value;
}

function isEditableTarget(target) {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export default function TutorialImageToolbar({
  meta,
  lineIndex,
  onMetaChange,
  onJoinPrevious,
  onSetStandalone,
  hasPreviousImage = false,
  onClose,
}) {
  const [widthInput, setWidthInput] = useState('');
  const [heightInput, setHeightInput] = useState('');

  const offsetX = meta?.offsetX || 0;
  const offsetY = meta?.offsetY || 0;

  useEffect(() => {
    setWidthInput(meta?.width ? String(meta.width) : '');
    setHeightInput(meta?.height ? String(meta.height) : '');
  }, [meta?.width, meta?.height, lineIndex]);

  const applyMeta = useCallback((patch) => {
    if (!meta) return;
    onMetaChange?.(lineIndex, { ...meta, ...patch });
  }, [lineIndex, meta, onMetaChange]);

  const nudgeOffset = useCallback((dx, dy) => {
    const nextX = offsetX + dx;
    const nextY = offsetY + dy;
    applyMeta({
      offsetX: nextX || null,
      offsetY: nextY || null,
    });
  }, [applyMeta, offsetX, offsetY]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Escape'].includes(event.key)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
        return;
      }
      if (isEditableTarget(event.target)) return;

      event.preventDefault();
      const step = event.shiftKey ? NUDGE_STEP_FINE : NUDGE_STEP;
      const deltaMap = {
        ArrowUp: [0, -step],
        ArrowDown: [0, step],
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
      };
      const [dx, dy] = deltaMap[event.key];
      nudgeOffset(dx, dy);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nudgeOffset, onClose]);

  const commitWidthInput = () => {
    const width = parseDimensionInput(widthInput);
    setWidthInput(width ? String(width) : '');
    applyMeta({ width });
  };

  const commitHeightInput = () => {
    const height = parseDimensionInput(heightInput);
    setHeightInput(height ? String(height) : '');
    applyMeta({ height });
  };

  if (!meta) return null;

  return (
    <div
      className="tutorial-image-toolbar"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="tutorial-image-toolbar__head">
        <span className="tutorial-image-toolbar__title">图片调整</span>
        <button type="button" className="tutorial-image-toolbar__close" aria-label="关闭" onClick={onClose}>
          <X size={16} />
        </button>
      </div>
      <div className="tutorial-image-toolbar__body">
        <div className="tutorial-image-toolbar__row">
          <div className="tutorial-image-toolbar__group">
            <span className="tutorial-image-toolbar__label">布局</span>
            {hasPreviousImage && !meta.row ? (
              <button
                type="button"
                className="tutorial-image-toolbar__chip tutorial-image-toolbar__chip--with-icon"
                title="与上一张图片并排显示（需宽度足够）"
                onClick={onJoinPrevious}
              >
                <Columns2 size={14} />
                与上一张并排
              </button>
            ) : null}
            {meta.row ? (
              <button
                type="button"
                className="tutorial-image-toolbar__chip tutorial-image-toolbar__chip--with-icon tutorial-image-toolbar__chip--active"
                title="改为独立成行"
                onClick={onSetStandalone}
              >
                <Square size={14} />
                独立成行
              </button>
            ) : null}
            {!hasPreviousImage && !meta.row ? (
              <span className="tutorial-image-toolbar__hint">首张图片无法并排</span>
            ) : null}
          </div>
        </div>
        <div className="tutorial-image-toolbar__row">
          <div className="tutorial-image-toolbar__group">
            <span className="tutorial-image-toolbar__label">对齐</span>
            {[
              { key: 'left', icon: AlignLeft, title: '居左' },
              { key: 'center', icon: AlignCenter, title: '居中' },
              { key: 'right', icon: AlignRight, title: '居右' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={`tutorial-image-toolbar__btn${meta.align === item.key ? ' tutorial-image-toolbar__btn--active' : ''}`}
                  title={item.title}
                  onClick={() => applyMeta({ align: item.key })}
                >
                  <Icon size={14} />
                </button>
              );
            })}
          </div>
          <span className="tutorial-image-toolbar__divider" aria-hidden="true" />
          <div className="tutorial-image-toolbar__group">
            <span className="tutorial-image-toolbar__label">位置</span>
            <div className="tutorial-image-toolbar__nudge">
              <button type="button" className="tutorial-image-toolbar__btn" title="上移" onClick={() => nudgeOffset(0, -NUDGE_STEP)}>
                <ArrowUp size={14} />
              </button>
              <div className="tutorial-image-toolbar__nudge-row">
                <button type="button" className="tutorial-image-toolbar__btn" title="左移" onClick={() => nudgeOffset(-NUDGE_STEP, 0)}>
                  <ArrowLeft size={14} />
                </button>
                <button type="button" className="tutorial-image-toolbar__btn" title="下移" onClick={() => nudgeOffset(0, NUDGE_STEP)}>
                  <ArrowDown size={14} />
                </button>
                <button type="button" className="tutorial-image-toolbar__btn" title="右移" onClick={() => nudgeOffset(NUDGE_STEP, 0)}>
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
            {(offsetX || offsetY) ? (
              <span className="tutorial-image-toolbar__offset">{offsetX}px, {offsetY}px</span>
            ) : null}
            {(offsetX || offsetY) ? (
              <button type="button" className="tutorial-image-toolbar__chip" onClick={() => applyMeta({ offsetX: null, offsetY: null })}>
                重置
              </button>
            ) : null}
            <span className="tutorial-image-toolbar__hint">方向键移动，Shift 微调</span>
          </div>
        </div>
        <div className="tutorial-image-toolbar__row">
          <div className="tutorial-image-toolbar__group">
            <span className="tutorial-image-toolbar__label">宽度</span>
            <button
              type="button"
              className={`tutorial-image-toolbar__chip${!meta.width ? ' tutorial-image-toolbar__chip--active' : ''}`}
              onClick={() => {
                setWidthInput('');
                applyMeta({ width: null });
              }}
            >
              自适应
            </button>
            {WIDTH_PRESETS.map((width) => (
              <button
                key={width}
                type="button"
                className={`tutorial-image-toolbar__chip${meta.width === width ? ' tutorial-image-toolbar__chip--active' : ''}`}
                onClick={() => {
                  setWidthInput(String(width));
                  applyMeta({ width });
                }}
              >
                {width}px
              </button>
            ))}
          </div>
          <span className="tutorial-image-toolbar__divider" aria-hidden="true" />
          <div className="tutorial-image-toolbar__group">
            <span className="tutorial-image-toolbar__label">自定义</span>
            <label className="tutorial-image-toolbar__dimension">
              <span className="tutorial-image-toolbar__dimension-label">宽</span>
              <input
                type="number"
                min="1"
                className="tutorial-image-toolbar__dimension-input"
                value={widthInput}
                placeholder="自适应"
                onChange={(event) => setWidthInput(event.target.value)}
                onBlur={commitWidthInput}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitWidthInput();
                  }
                }}
              />
              <span className="tutorial-image-toolbar__dimension-unit">px</span>
            </label>
            <label className="tutorial-image-toolbar__dimension">
              <span className="tutorial-image-toolbar__dimension-label">高</span>
              <input
                type="number"
                min="1"
                className="tutorial-image-toolbar__dimension-input"
                value={heightInput}
                placeholder="自适应"
                onChange={(event) => setHeightInput(event.target.value)}
                onBlur={commitHeightInput}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitHeightInput();
                  }
                }}
              />
              <span className="tutorial-image-toolbar__dimension-unit">px</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
